
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      let errorMessage = "Something went wrong";
      
      // Check for specific error types
      if (this.state.error) {
        if (this.state.error.message.includes('timeout')) {
          errorMessage = "Request timed out. The server might be busy or there may be network issues.";
        } else if (this.state.error.message.includes('offline') || this.state.error.message.includes('network')) {
          errorMessage = "Network connection issue. Please check your internet connection.";
        } else if (this.state.error.message.includes('permission')) {
          errorMessage = "You don't have permission to access this resource.";
        }
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
          <div className="mb-6 text-red-500">
            <AlertCircle size={48} />
          </div>
          <h2 className="text-2xl font-bold mb-2">An Error Occurred</h2>
          <p className="text-muted-foreground mb-6">{errorMessage}</p>
          <Button 
            onClick={this.handleRefresh}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh Page
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            If the problem persists, please contact support.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
